import { useState } from "react";
import { Link } from "react-router-dom";
import { useCanWrite } from "@/app/auth";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { channelGroups, channels, type ChannelGroup } from "@/app/master";
import { loadingOf, nameOf, useList, usePrimaryBackup } from "@/app/queries";
import { Alert, Button, EmptyState, Input as TextInput, PageHeader, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { ChannelGroupDrawer } from "./ChannelGroupDrawer";
import { membersSummary, strategySummary } from "./channelGroupDraft";
import { PrimaryBackupMarks } from "./PrimaryBackupMarks";

// 策略与成员的摘要可能很长，允许换行，别把状态列挤出表格
const wrap = { whiteSpace: "normal" } as const;

/** 通道组：一组成员与选择它们的策略（docs/03 4.1、docs/10 第 3 节，决策 D69），被路由明细引用为发送目标，也可以是别的组的成员。保存即生效。 */
export function ChannelGroups() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: ChannelGroup | null } | null>(null);
  const pb = usePrimaryBackup(); // 状态列标出切到备用、验证中的运营商，详情在 /channel-groups/{id}
  const list = useList("channel-groups", channelGroups);
  const channelList = useList("channels", channels).data;
  const names = { group: (id: number) => nameOf(list.data, id), channel: (id: number) => nameOf(channelList, id) };
  const keyword = q.trim().toLowerCase();
  const shown = (list.data ?? []).filter((g) => !keyword || g.name.toLowerCase().includes(keyword));

  return (
    <>
      <PageHeader title="通道组合" description="一组成员加选择策略，可被多条路由复用；成员是通道或另一个组合，最多嵌套 3 层，保存即生效。点名称看成员表现与选路状态。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建通道组合</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      {pb.error && <Alert tone="warning" title="主备状态读取失败">状态列暂未标出主备组合切到备用或验证中的运营商：{pb.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{shown.length} 个</ToolbarCount>}>
        <TextInput size="sm" className="ax-search" placeholder="搜索通道组合名称" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {shown.length ? (
          <Table>
            <thead><tr><Th w="id">ID</Th><Th w="name">名称</Th><Th w="wide">策略</Th><Th w="wide">成员</Th><Th w={240}>状态</Th><Th numeric w={72}>版本</Th><Th /></tr></thead>
            <tbody>
              {shown.map((g) => (
                <tr key={g.id}>
                  <td className="ax-mono">{g.id}</td><td><Link className="ax-link" to={`/channel-groups/${g.id}`}>{g.name}</Link></td><td className="ax-secondary" style={wrap}>{strategySummary(g, names)}</td><td className="ax-secondary" style={wrap}>{membersSummary(g, names)}</td>
                  <td><EntityStatusPill status={g.status} />{g.strategy === "primary_backup" && <PrimaryBackupMarks pb={pb.data} groupID={g.id} />}</td><td className="ax-num">{g.version}</td>
                  <td><Button variant="ghost" size="sm" onClick={() => setEditing({ row: g })}>{canWrite ? "编辑" : "查看"}</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title="没有通道组合">先建通道，再把它们组成通道组合，路由规则的目标引用通道组合。</EmptyState>
        )}
      </TableWrap>
      {editing && <ChannelGroupDrawer row={editing.row} groups={list.data} channels={channelList} onClose={() => setEditing(null)} />}
    </>
  );
}
